import type { SkuSummary } from "@dishour/shared";
import { useState } from "react";
import { useCreateSku, useSkus, useSuppliers } from "../../api/admin.js";
import { AdminShell } from "../../components/AdminShell.js";
import { DataTable, type Column } from "../../components/DataTable.js";
import { Field, buttonClass, inputClass } from "../../components/Field.js";
import { SkuDivisibilityFields } from "../../components/SkuDivisibilityFields.js";

export function AdminSkusPage() {
  const skus = useSkus();
  const suppliers = useSuppliers();
  const createSku = useCreateSku();
  const [form, setForm] = useState({
    supplierId: "",
    nameKo: "",
    nameEn: "",
    unit: "박스",
    packSize: "",
    aliases: "",
    isDivisible: false,
    qtyStep: "1",
  });

  const columns: Column<SkuSummary>[] = [
    { key: "nameKo", label: "품목명", sortValue: (s) => s.nameKo, render: (s) => s.nameKo },
    { key: "supplier", label: "공급업체", sortValue: (s) => s.supplier.name, render: (s) => s.supplier.name },
    { key: "unit", label: "단위", sortValue: (s) => s.unit, render: (s) => s.unit },
    { key: "packSize", label: "포장규격", render: (s) => s.packSize ?? "—" },
    {
      key: "step",
      label: "주문 단위",
      render: (s) => (s.isDivisible ? `${s.qtyStep} 단위 소수 주문 가능` : "정수 단위만"),
    },
  ];

  return (
    <AdminShell title="품목 관리">
      <form
        className="mb-6 grid gap-4 rounded-xl border border-stone-200 bg-white p-4 md:grid-cols-2"
        onSubmit={(e) => {
          e.preventDefault();
          createSku.mutate({
            supplierId: form.supplierId,
            nameKo: form.nameKo,
            nameEn: form.nameEn || null,
            unit: form.unit,
            packSize: form.packSize || null,
            isDivisible: form.isDivisible,
            qtyStep: form.qtyStep,
            aliases: form.aliases.split(",").map((s) => s.trim()).filter(Boolean),
          });
        }}
      >
        <Field label="공급업체" htmlFor="supplierId">
          <select
            id="supplierId"
            required
            className={inputClass}
            value={form.supplierId}
            onChange={(e) => setForm({ ...form, supplierId: e.target.value })}
          >
            <option value="">선택하세요</option>
            {suppliers.data?.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="품목명 (한글)" htmlFor="nameKo">
          <input
            id="nameKo"
            required
            className={inputClass}
            value={form.nameKo}
            onChange={(e) => setForm({ ...form, nameKo: e.target.value })}
          />
        </Field>
        <Field label="품목명 (영문)" htmlFor="nameEn">
          <input
            id="nameEn"
            className={inputClass}
            value={form.nameEn}
            onChange={(e) => setForm({ ...form, nameEn: e.target.value })}
          />
        </Field>
        <Field label="단위 (박스, 개, 팩 …)" htmlFor="unit">
          <input
            id="unit"
            required
            className={inputClass}
            value={form.unit}
            onChange={(e) => setForm({ ...form, unit: e.target.value })}
          />
        </Field>
        <Field label="포장 규격 (예: 10lb)" htmlFor="packSize">
          <input
            id="packSize"
            className={inputClass}
            value={form.packSize}
            onChange={(e) => setForm({ ...form, packSize: e.target.value })}
          />
        </Field>
        <div className="md:col-span-2">
          <SkuDivisibilityFields
            isDivisible={form.isDivisible}
            qtyStep={form.qtyStep}
            onChange={(next) => setForm({ ...form, ...next })}
          />
        </div>
        <Field label="다른 표기 (쉼표로 구분: 나빠배추, napa)" htmlFor="aliases">
          <input
            id="aliases"
            className={inputClass}
            value={form.aliases}
            onChange={(e) => setForm({ ...form, aliases: e.target.value })}
          />
        </Field>
        {createSku.error ? (
          <p role="alert" className="text-sm text-red-600 md:col-span-2">
            {(createSku.error as Error).message}
          </p>
        ) : null}
        <button type="submit" className={`${buttonClass} md:col-span-2`} disabled={createSku.isPending}>
          품목 추가
        </button>
      </form>

      <DataTable
        columns={columns}
        rows={skus.data ?? []}
        rowKey={(s) => s.id}
        emptyMessage="등록된 품목이 없습니다."
        isPending={skus.isPending}
      />
    </AdminShell>
  );
}
